import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { MarkdownContent, TextContent } from '../components/MarkdownContent';
import { MediaViewer, VideoCard } from '../components/MediaViewer';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Trophy, 
  DollarSign, 
  FileText, 
  Sparkles, 
  Image as ImageIcon,
  Video,
  Loader2,
  Target,
  Users,
  Zap,
  ExternalLink,
  Download
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

export default function WinningProductDetail() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [winningDetail, setWinningDetail] = useState(null);
  const [winningImages, setWinningImages] = useState(null);
  const [marketing, setMarketing] = useState(null);
  
  // Video viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: '', title: '' });
  const [allVideos, setAllVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [productRes, detailRes, imagesRes, marketingRes] = await Promise.all([
        supabase.from('products').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_images').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('product_marketing_angles').select('*').eq('ecom_id', ecomId).single(),
      ]);

      setProduct(productRes.data);
      setWinningDetail(detailRes.data);
      setWinningImages(imagesRes.data);
      setMarketing(marketingRes.data);
    } catch (error) {
      console.error('Error fetching winning product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout productName="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!winningDetail) {
    return (
      <Layout>
        <div className="text-center py-16">
          <Trophy className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Product not found</p>
          <button
            onClick={() => navigate('/winning-products')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Winning Products
          </button>
        </div>
      </Layout>
    );
  }

  const displayName = winningDetail.product_name || ecomId;
  const allImages = [
    winningImages?.image1,
    winningImages?.image2,
    winningImages?.image3,
    winningImages?.image4,
    winningImages?.image5,
  ].filter(Boolean);

  // Collect videos from winning_product_images table
  const productVideos = [
    winningImages?.video1 ? { url: winningImages.video1, title: 'Product Video 1' } : null,
    winningImages?.video2 ? { url: winningImages.video2, title: 'Product Video 2' } : null,
  ].filter(Boolean);

  const openVideoViewer = (url, title, index = 0) => {
    setCurrentVideo({ url, title });
    setAllVideos(productVideos);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const handleNavigate = (newIndex) => {
    if (productVideos[newIndex]) {
      setCurrentIndex(newIndex);
      setCurrentVideo({
        url: productVideos[newIndex].url,
        title: productVideos[newIndex].title
      });
    }
  };

  return (
    <Layout productName={displayName}>
      <div data-testid="winning-product-detail">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => navigate('/winning-products')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="flex items-center gap-3 md:gap-4 min-w-0">
                {allImages[0] ? (
                  <img
                    src={allImages[0]}
                    alt={displayName}
                    className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg border-2 border-slate-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-6 h-6 md:w-8 md:h-8 text-slate-400" />
                  </div>
                )}
                
                <div className="min-w-0">
                  <h1 className="text-lg md:text-2xl font-bold text-[#1E1B4B] font-['Manrope'] truncate">
                    {displayName}
                  </h1>
                  <div className="flex items-center gap-2 md:gap-3 mt-1 flex-wrap">
                    <StatusBadge status={winningDetail.status} lane="winning" />
                    {winningDetail.price && (
                      <span className="text-lg font-bold text-emerald-600">
                        ${winningDetail.price}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {product?.url && (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-2 text-sm md:text-base self-start md:self-auto"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">View Original</span>
                <span className="sm:hidden">View</span>
              </a>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="details" className="space-y-6">
          <TabsList className="bg-slate-100 p-1 w-full md:w-auto flex">
            <TabsTrigger value="details" className="data-[state=active]:bg-white flex items-center gap-1 md:gap-2 flex-1 md:flex-none text-xs md:text-sm">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Product & Images</span>
              <span className="sm:hidden">Details</span>
            </TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-white flex items-center gap-1 md:gap-2 flex-1 md:flex-none text-xs md:text-sm">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Marketing Strategy</span>
              <span className="sm:hidden">Marketing</span>
            </TabsTrigger>
          </TabsList>

          {/* Product & Images Tab */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Images Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#1E1B4B] flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Hero Images
                  </h3>
                  {winningImages && (
                    <StatusBadge status={winningImages.status} lane="winning" />
                  )}
                </div>
                
                {allImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {allImages.map((img, i) => (
                      <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                        <img src={img} alt={`Image ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm italic">No images generated yet</p>
                )}
              </div>

              {/* Videos Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#1E1B4B] flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Product Videos
                  </h3>
                  {productVideos.length > 0 && (
                    <span className="text-sm text-slate-500">{productVideos.length} videos</span>
                  )}
                </div>
                
                {productVideos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {productVideos.map((video, i) => (
                      <VideoCard
                        key={i}
                        url={video.url}
                        label={video.title}
                        onView={() => openVideoViewer(video.url, video.title, i)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Video className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">No videos generated yet</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Price & Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Price Card */}
              {winningDetail.price && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-emerald-600" />
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">Selling Price</p>
                      <p className="text-4xl font-bold text-emerald-700">${winningDetail.price}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Description
                </h4>
                <MarkdownContent 
                  content={winningDetail.description} 
                  title="Product Description"
                  maxHeight={200}
                />
              </div>
            </div>

            {/* Features & How to Use */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Features */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Features
                </h4>
                <MarkdownContent 
                  content={winningDetail.features} 
                  title="Product Features"
                  maxHeight={200}
                />
              </div>

              {/* How to Use */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-3">How to Use</h4>
                <MarkdownContent 
                  content={winningDetail.how_to_use} 
                  title="How to Use"
                  maxHeight={200}
                />
              </div>
            </div>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing" className="space-y-6">
            {marketing ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#1E1B4B]">Marketing Strategy</h3>
                  <StatusBadge status={marketing.status} lane="research" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Marketing Angle */}
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-5 h-5 text-violet-600" />
                      <h4 className="font-semibold text-violet-700">Marketing Angle</h4>
                    </div>
                    <MarkdownContent 
                      content={marketing.marketing_angle} 
                      title="Marketing Angle"
                      maxHeight={200}
                    />
                  </div>

                  {/* Competitive Edge */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-semibold text-emerald-700">Competitive Edge</h4>
                    </div>
                    <MarkdownContent 
                      content={marketing.competitive_edge} 
                      title="Competitive Edge"
                      maxHeight={200}
                    />
                  </div>
                </div>

                {/* Customer Personas */}
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-cyan-600" />
                    <h4 className="font-semibold text-cyan-700">Customer Personas</h4>
                  </div>
                  <MarkdownContent 
                    content={marketing.customer_personas} 
                    title="Customer Personas"
                    maxHeight={250}
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Target className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">No marketing angles generated yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Video Viewer */}
        <MediaViewer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          mediaUrl={currentVideo.url}
          mediaType="video"
          title={currentVideo.title}
          allMedia={productVideos}
          currentIndex={currentIndex}
          onNavigate={handleNavigate}
        />
      </div>
    </Layout>
  );
}
